import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { postRequest } from "../../services/api";
import DashboardLayout from "../DashboardLayout/DashboardLayout";
import "./ChangePassword.css";

const ChangePassword: React.FC = () => {
  const [form, setForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [showPwd, setShowPwd] = useState({ current: false, newPwd: false, confirm: false });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [msgInfo, setMsgInfo] = useState<string | null>(null);
  const [msgOk, setMsgOk] = useState(true);
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: "" });
  };

  const togglePwd = (field: keyof typeof showPwd) => {
    setShowPwd(p => ({ ...p, [field]: !p[field] }));
  };

  const getStrength = (pwd: string) => {
    if (!pwd) return 0;
    let score = 0;
    if (pwd.length >= 8)       score++;
    if (/[A-Z]/.test(pwd))     score++;
    if (/[0-9]/.test(pwd))     score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    return score;
  };
  const strength = getStrength(form.newPassword);
  const strengthLabel = ["", "Weak", "Fair", "Good", "Strong"][strength];
  const strengthColor = ["", "#ef4444", "#f59e0b", "#0d9e8a", "#16a34a"][strength];

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.currentPassword) e.currentPassword = "Current password is required";
    if (form.newPassword.length < 6) e.newPassword = "New password must be at least 6 characters";
    if (form.newPassword !== form.confirmPassword) e.confirmPassword = "Passwords do not match";
    if (form.newPassword === form.currentPassword) e.newPassword = "New password must differ from current password";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setMsgInfo(null);
    try {
      await postRequest("/auth/change-password", {
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });
      setMsgOk(true);
      setMsgInfo("Password changed successfully! Please log in again.");
      setForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (err: any) {
      setMsgOk(false);
      setMsgInfo(err?.response?.data?.message || "Failed to change password. Please check your current password.");
    } finally {
      setLoading(false);
    }
  };

  const EyeIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  );
  const EyeOffIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );

  const PasswordField = ({
    label, name, value, showKey, placeholder
  }: { label: string; name: string; value: string; showKey: keyof typeof showPwd; placeholder: string }) => (
    <div className="cp-field">
      <label className="cp-label">{label}</label>
      <div className="cp-input-wrap">
        <svg className="cp-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
        <input
          type={showPwd[showKey] ? "text" : "password"}
          name={name}
          className={`cp-input${errors[name] ? " cp-input-err" : ""}`}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          autoComplete="new-password"
        />
        <button type="button" className="cp-eye" onClick={() => togglePwd(showKey)} tabIndex={-1}>
          {showPwd[showKey] ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>
      {errors[name] && <span className="cp-err">{errors[name]}</span>}
    </div>
  );

  return (
    <>
      <Helmet><title>Change Password - Acadebility</title></Helmet>
      <DashboardLayout pageTitle="Change Password" activePath="/change-password">

        <div className="cp-layout">

          {/* ── Left: form ── */}
          <div className="cp-form-card dsh-card">
            <div className="cp-card-head">
              <div className="cp-card-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </div>
              <div>
                <h2 className="cp-card-title">Change Password</h2>
                <p className="cp-card-sub">Use a strong password to keep your account secure</p>
              </div>
            </div>

            {msgInfo && (
              <div className={`cp-alert${msgOk ? " cp-alert-ok" : " cp-alert-err"}`}>
                {msgOk ? "✅" : "⚠️"} {msgInfo}
              </div>
            )}

            <form className="cp-form" onSubmit={handleSubmit}>
              <PasswordField
                label="Current Password"
                name="currentPassword"
                value={form.currentPassword}
                showKey="current"
                placeholder="Enter your current password"
              />

              <PasswordField
                label="New Password"
                name="newPassword"
                value={form.newPassword}
                showKey="newPwd"
                placeholder="Enter a strong new password"
              />

              {/* Strength bar */}
              {form.newPassword.length > 0 && (
                <div className="cp-strength">
                  <div className="cp-strength-bar">
                    {[1,2,3,4].map(i => (
                      <div
                        key={i}
                        className="cp-strength-seg"
                        style={{ background: i <= strength ? strengthColor : "#e5e7eb" }}
                      />
                    ))}
                  </div>
                  <span className="cp-strength-label" style={{ color: strengthColor }}>
                    {strengthLabel}
                  </span>
                </div>
              )}

              <PasswordField
                label="Confirm New Password"
                name="confirmPassword"
                value={form.confirmPassword}
                showKey="confirm"
                placeholder="Re-enter your new password"
              />

              <button type="submit" className="cp-submit-btn" disabled={loading}>
                {loading
                  ? <><span className="cp-spinner" /> Updating…</>
                  : <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                      </svg>
                      Update Password
                    </>
                }
              </button>
            </form>
          </div>

          {/* ── Right: tips card ── */}
          <div className="cp-tips-card dsh-card">
            <h3 className="cp-tips-title">Password Security Tips</h3>
            <div className="cp-tips-list">
              {[
                { icon: "✅", text: "Use at least 8 characters" },
                { icon: "✅", text: "Include uppercase and lowercase letters" },
                { icon: "✅", text: "Add numbers for extra security" },
                { icon: "✅", text: "Use special characters like @, #, $" },
                { icon: "❌", text: "Don't use personal info (name, birthday)" },
                { icon: "❌", text: "Avoid common words like 'password'" },
                { icon: "❌", text: "Don't reuse passwords across accounts" },
              ].map(tip => (
                <div className="cp-tip-item" key={tip.text}>
                  <span className="cp-tip-icon">{tip.icon}</span>
                  <span className="cp-tip-text">{tip.text}</span>
                </div>
              ))}
            </div>

            <div className="cp-security-score">
              <div className="cp-score-label">Password Strength</div>
              <div className="cp-score-bar-wrap">
                <div className="cp-score-track">
                  <div
                    className="cp-score-fill"
                    style={{
                      width: form.newPassword ? `${(strength / 4) * 100}%` : "0%",
                      background: strengthColor || "#e5e7eb"
                    }}
                  />
                </div>
                <span className="cp-score-val" style={{ color: strengthColor || "#b0c4c1" }}>
                  {form.newPassword ? strengthLabel : "—"}
                </span>
              </div>
            </div>

            <div className="cp-help-note">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              If you forget your password, use the{" "}
              <a href="/forgot-password" className="cp-help-link">Forgot Password</a>{" "}
              flow from the login page.
            </div>
          </div>

        </div>
      </DashboardLayout>
    </>
  );
};

export default ChangePassword;
